import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface AnalysisStatusProps {
  brandId: number;
  onComplete?: (reportId: number) => void;
}

interface StatusData {
  status: string;
  progress: number;
  current_step: string;
  estimated_time_remaining: number;
  report_id?: number;
}

const AnalysisStatus: React.FC<AnalysisStatusProps> = ({ brandId, onComplete }) => {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dashboard.llmseo.fi';

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get<StatusData>(`${API_BASE_URL}/api/analysis/status/${brandId}`);
        setStatus(response.data);

        if (response.data.status === 'completed' && response.data.report_id && onComplete) {
          onComplete(response.data.report_id);
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch analysis status');
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
    
    // Poll every 5 seconds if analysis is in progress
    const interval = setInterval(() => {
      if (status?.status !== 'completed' && status?.status !== 'failed') {
        fetchStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [brandId, onComplete, status?.status, API_BASE_URL]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  if (!status) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'processing': return 'text-blue-600 bg-blue-50';
      case 'completed': return 'text-green-600 bg-green-50';
      case 'failed': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStepDescription = (step: string) => {
    const steps: { [key: string]: string } = {
      'crawling': 'Gathering web content and analyzing your brand presence',
      'ai_analysis': 'Querying AI models for brand perception insights',
      'report_generation': 'Compiling comprehensive analysis report',
      'completed': 'Analysis complete! Report ready for review'
    };
    return steps[step] || step;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis Progress</h3>
      
      <div className="space-y-4">
        {/* Status Badge */}
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status.status)}`}>
          {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${status.progress}%` }}
          ></div>
        </div>

        {/* Current Step */}
        <div className="text-sm text-gray-600">
          <p className="font-medium">{getStepDescription(status.current_step)}</p>
          {status.estimated_time_remaining > 0 && (
            <p className="text-gray-500 mt-1">
              Estimated time remaining: {Math.ceil(status.estimated_time_remaining / 60)} minutes
            </p>
          )}
        </div>

        {/* Progress Details */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">{status.progress}%</div>
            <div className="text-sm text-gray-500">Complete</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {status.estimated_time_remaining > 0 ? 
                `${Math.ceil(status.estimated_time_remaining / 60)}m` : 
                'Ready'
              }
            </div>
            <div className="text-sm text-gray-500">ETA</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">3</div>
            <div className="text-sm text-gray-500">AI Models</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisStatus;
